import React from 'react';
import { marriageCertificates as api } from '../../api/endpoints';
import { formatDate } from '../../utils/format';
import { FormLayout, Input } from '../../components/ui';
import type { RegisterConfig } from './registerConfig';
import RegisterMemberField from './MemberField';
import type { MarriageCertificate, Member } from '../../types';

/** Prohibition of Child Marriage Act, 2006 — the minimum ages in India. */
const GROOM_MIN_AGE = 21;
const BRIDE_MIN_AGE = 18;

const EMPTY = {
  registerNo: '', certificateNo: '',
  groomMemberId: '', brideMemberId: '',
  groomName: '', parentName: '', brideName: '',
  marriageDate: '', marriagePlace: '', minister: '', witness1: '', witness2: '',
  registrationDetails: '', remarks: '',
  /** Form-local: the picked spouses, for the age check and the warnings. */
  _groom: null as Member | null,
  _bride: null as Member | null
};

type Form = typeof EMPTY;

/** Whole years between two ISO dates, or null when either is missing/malformed. */
const yearsBetween = (fromISO?: string, toISO?: string): number | null => {
  if (!fromISO || !toISO) return null;
  const from = new Date(fromISO);
  const to = new Date(toISO);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) return null;
  let years = to.getFullYear() - from.getFullYear();
  const monthDiff = to.getMonth() - from.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && to.getDate() < from.getDate())) years -= 1;
  return years;
};

const Certificate = (r: MarriageCertificate) => (
  <div>
    <p>
      This is to certify that <strong>{r.groomName}</strong> and <strong>{r.brideName}</strong> were
      joined together in Holy Matrimony on <strong>{formatDate(r.marriageDate)}</strong> at{' '}
      <strong>{r.marriagePlace || '—'}</strong>, according to the rites and ceremonies of the Church.
    </p>

    <div style={{ marginTop: '20px' }}>
      {r.parentName && <div><strong>Parent:</strong> {r.parentName}</div>}
      <div><strong>Officiating Minister:</strong> {r.minister || '—'}</div>
      <div><strong>Witnesses:</strong> {[r.witness1, r.witness2].filter(Boolean).join('  •  ') || '—'}</div>
      {r.registrationDetails && <div><strong>Civil Registration:</strong> {r.registrationDetails}</div>}
      {r.remarks && <div><strong>Remarks:</strong> {r.remarks}</div>}
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '70px' }}>
      <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '5px' }}>Witness 1</div>
      <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '5px' }}>Minister</div>
      <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '5px' }}>Witness 2</div>
    </div>
  </div>
);

/**
 * The one hard block in the registers. A marriage certificate is a legal
 * document, so an underage one is refused rather than questioned — and the
 * backend refuses it again, since this check is only possible when the spouse
 * is a linked member with a date of birth on file.
 */
const underageMessage = (f: Form): string | null => {
  const checks: Array<[Member | null, number, string]> = [
    [f._groom, GROOM_MIN_AGE, 'groom'],
    [f._bride, BRIDE_MIN_AGE, 'bride']
  ];
  for (const [member, minAge, role] of checks) {
    if (!member) continue;
    const age = yearsBetween(member.dateOfBirth, f.marriageDate);
    if (age !== null && age < minAge) {
      return `The ${role} (${member.name || member.memberId}) would be ${age} on this date. `
        + `The legal minimum age is ${minAge}.`;
    }
  }
  return null;
};

const warn = (f: Form): string | null => {
  const notes: string[] = [];
  ([[f._groom, 'groom'], [f._bride, 'bride']] as Array<[Member | null, string]>)
    .forEach(([member, role]) => {
      if (!member) return;
      if (member.dateOfMarriage && member.dateOfMarriage !== f.marriageDate) {
        notes.push(
          `The ${role} (${member.name || member.memberId}) is already recorded as married on `
          + `${formatDate(member.dateOfMarriage)}. Saving will replace that date on their record.`
        );
      }
    });
  return notes.length ? notes.join(' ') : null;
};

const config: RegisterConfig<MarriageCertificate, Form> = {
  icon: '💍',
  title: 'Marriage Certificate Register',
  prefix: 'MCT',
  label: 'Marriage Entry',
  resource: api,
  emptyForm: EMPTY,
  toForm: (r) => ({
    ...EMPTY,
    registerNo: r.registerNo || '', certificateNo: r.certificateNo || '',
    groomMemberId: r.groomMemberId || '', brideMemberId: r.brideMemberId || '',
    groomName: r.groomName || '', parentName: r.parentName || '', brideName: r.brideName || '',
    marriageDate: r.marriageDate || '', marriagePlace: r.marriagePlace || '',
    minister: r.minister || '', witness1: r.witness1 || '', witness2: r.witness2 || '',
    registrationDetails: r.registrationDetails || '', remarks: r.remarks || ''
  }),
  validate: (f) => {
    if (!f.groomName.trim() || !f.brideName.trim()) return 'Both the groom and bride names are required.';
    if (!f.marriageDate) return 'The date of marriage is required.';
    // A marriage certificate is only valid when witnessed.
    if (!f.witness1.trim() || !f.witness2.trim()) return 'Two witnesses are required on a marriage certificate.';
    if (f.groomMemberId && f.groomMemberId === f.brideMemberId) {
      return 'The groom and bride cannot be the same member.';
    }
    return underageMessage(f);
  },
  warn,
  rowDate: (r) => r.marriageDate,
  searchKeys: ['recordId', 'groomName', 'brideName', 'groomMemberId', 'brideMemberId', 'registerNo', 'certificateNo', 'minister', 'marriagePlace'],
  searchPlaceholder: 'Search by Groom, Bride, Register or Certificate No...',
  columns: [
    {
      key: 'recordId',
      header: 'Date & Reg No.',
      nowrap: true,
      render: (r: MarriageCertificate) => (
        <>
          <strong>{formatDate(r.marriageDate)}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.registerNo || r.recordId}</div>
        </>
      )
    },
    {
      key: 'couple',
      header: 'Couple',
      render: (r: MarriageCertificate) => (
        <>
          <strong>{r.groomName}</strong>
          <div style={{ fontSize: '11px', opacity: 0.7 }}>&amp; {r.brideName}</div>
        </>
      )
    },
    { key: 'marriagePlace', header: 'Place', render: (r: MarriageCertificate) => r.marriagePlace || '-' },
    { key: 'minister', header: 'Minister', render: (r: MarriageCertificate) => r.minister || '-' },
    {
      key: 'witnesses',
      header: 'Witnesses',
      render: (r: MarriageCertificate) => [r.witness1, r.witness2].filter(Boolean).join(', ') || '-'
    },
    { key: 'certificateNo', header: 'Cert No.', render: (r: MarriageCertificate) => r.certificateNo || '-' }
  ],
  printModule: 'Marriage',
  printDocumentType: 'MARRIAGE_CERTIFICATE',
  listDocumentType: 'MARRIAGE_REGISTER',
  printData: (r: any) => ({
    registerNo: r.registerNo, certificateNo: r.certificateNo,
    groomName: r.groomName, brideName: r.brideName, parentName: r.parentName,
    marriageDate: formatDate(r.marriageDate), marriagePlace: r.marriagePlace,
    minister: r.minister, witness1: r.witness1, witness2: r.witness2,
    registrationDetails: r.registrationDetails, remarks: r.remarks, recordId: r.recordId
  }),
  renderDocument: Certificate,
  fields: (form, setForm, ctx) => (
    <>
      <FormLayout.Section title="Couple" />
      <FormLayout.Grid>
        {/* Picking a spouse is optional throughout: an outsider marrying into
            the parish is recorded by name, and only linked spouses have their
            member record updated. */}
        <RegisterMemberField
          form={form}
          setForm={setForm}
          idKey="groomMemberId"
          memberKey="_groom"
          label="Groom (member)"
          hint="Leave blank if the groom is not on the rolls."
          filter={(m) => m.gender !== 'Female'}
          isDarkMode={ctx.isDarkMode}
          autofill={(member) => ({
            groomName: member?.name || '',
            parentName: member?.relativeName || ''
          })}
        />
        <RegisterMemberField
          form={form}
          setForm={setForm}
          idKey="brideMemberId"
          memberKey="_bride"
          label="Bride (member)"
          hint="Leave blank if the bride is not on the rolls."
          filter={(m) => m.gender !== 'Male'}
          isDarkMode={ctx.isDarkMode}
          autofill={(member) => ({ brideName: member?.name || '' })}
        />
        <Input
          label="Groom's Name" required placeholder="e.g. Samuel David"
          value={form.groomName} onChange={(e) => setForm({ ...form, groomName: e.target.value })}
        />
        <Input
          label="Bride's Name" required placeholder="e.g. Anna Rachel"
          value={form.brideName} onChange={(e) => setForm({ ...form, brideName: e.target.value })}
        />
        {/* The old form called this `fatherMotherName`, which the backend had
            no column for and silently dropped. It maps to `parentName` here. */}
        <Input
          label="Father / Mother Name" placeholder="As named on the certificate"
          value={form.parentName} onChange={(e) => setForm({ ...form, parentName: e.target.value })}
        />
        <Input
          label="Date of Marriage" type="date" required
          value={form.marriageDate} onChange={(e) => setForm({ ...form, marriageDate: e.target.value })}
        />
        <Input
          label="Place of Marriage" placeholder="e.g. CSI Christ Church"
          value={form.marriagePlace} onChange={(e) => setForm({ ...form, marriagePlace: e.target.value })}
        />
        <Input
          label="Officiating Minister" placeholder="e.g. Rev. David Thomas"
          value={form.minister} onChange={(e) => setForm({ ...form, minister: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Witnesses" />
      <FormLayout.Grid>
        <Input
          label="Witness 1" required placeholder="Full name"
          value={form.witness1} onChange={(e) => setForm({ ...form, witness1: e.target.value })}
        />
        <Input
          label="Witness 2" required placeholder="Full name"
          value={form.witness2} onChange={(e) => setForm({ ...form, witness2: e.target.value })}
        />
      </FormLayout.Grid>

      <FormLayout.Section title="Register Details" />
      <FormLayout.Grid>
        <Input
          label="Marriage Register Number" placeholder="e.g. Vol 3, Page 41"
          value={form.registerNo} onChange={(e) => setForm({ ...form, registerNo: e.target.value })}
        />
        <Input
          label="Certificate Number" placeholder="e.g. MC-2026-021"
          value={form.certificateNo} onChange={(e) => setForm({ ...form, certificateNo: e.target.value })}
        />
        <Input
          label="Civil Registration Details" fullWidth
          placeholder="Registrar office reference, where applicable"
          value={form.registrationDetails} onChange={(e) => setForm({ ...form, registrationDetails: e.target.value })}
        />
        <Input
          label="Remarks" type="textarea" rows={2} fullWidth
          value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
        />
      </FormLayout.Grid>
    </>
  )
};

export { config as certificateConfig };
